import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Snackbar,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Refresh,
  Warning as WarningIcon,
  CheckCircleOutline,
  HourglassEmpty,
  ContentCopy,
  FileDownload,
} from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useCompanyStore } from '@/stores/company-store';
import { useUIStore } from '@/stores/ui-store';
import { statusService, type StatusLogItem } from '@/services/status-service';
import { StatusDialog } from './StatusDialog';
import { useSnackbarStore } from '@certara/common-react-components';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/es';
import 'dayjs/locale/ja';

// Status pattern mapping to avoid redundant regex operations
const STATUS_PATTERNS = {
  LOW: /Processed[ -]+(Low|Information|Ignore|Prompt Message)/,
  HIGH: /Processed[ -]+(High|Error|Fail|High - CDER)/,
  MEDIUM: /Processed[ -]+(Medium|Best Practice|Warning)/,
} as const;

type StatusCategory = 'processing' | 'pending' | 'processed' | 'low' | 'high' | 'medium' | 'failed' | 'unknown';

const getStatusCategory = (status: string): StatusCategory => {
  if (status === 'Processing') return 'processing';
  if (status === 'Pending') return 'pending';
  if (status === 'Processed') return 'processed';
  if (STATUS_PATTERNS.LOW.test(status)) return 'low';
  if (STATUS_PATTERNS.HIGH.test(status)) return 'high';
  if (STATUS_PATTERNS.MEDIUM.test(status)) return 'medium';
  if (status.startsWith('Failed')) return 'failed';
  return 'unknown';
};

type SortDirection = 'asc' | 'desc';

interface ColumnDef {
  field: string;
  label: string;
  sortable: boolean;
  minWidth?: number;
}

export function ActivityDialog() {
  const { t, i18n: i18nInstance } = useTranslation('load');
  const { activeCompany } = useCompanyStore();

  // Only support users in the System space (company ID = 1) see the all-companies toggle.
  const canSeeAllCompanies = activeCompany?.Id === 1;

  const [allCompanies, setAllCompanies] = useState(false);

  // Reset allCompanies when leaving System space so it never leaks into non-System API calls.
  useEffect(() => {
    if (!canSeeAllCompanies) setAllCompanies(false);
  }, [canSeeAllCompanies]);

  const columns = useMemo<ColumnDef[]>(
    () => [
      { field: 'Status', label: t('activity.columnStatus'), sortable: true, minWidth: 160 },
      ...(allCompanies ? [{ field: 'CompanyName', label: t('activity.columnCompany'), sortable: false, minWidth: 140 }] : []),
      { field: 'SourceAppId', label: t('activity.columnApplication'), sortable: true, minWidth: 110 },
      { field: 'SourceSubId', label: t('activity.columnSequence'), sortable: true, minWidth: 100 },
      { field: 'Type', label: t('activity.columnType'), sortable: true, minWidth: 100 },
      { field: 'SubmissionType', label: t('activity.columnSequenceType'), sortable: true, minWidth: 130 },
      { field: 'ApplicationType', label: t('activity.columnApplicationType'), sortable: true, minWidth: 140 },
      { field: 'Processed', label: t('activity.columnProcessDateTime'), sortable: true, minWidth: 180 },
      { field: 'UserId', label: t('activity.columnUser'), sortable: true, minWidth: 100 },
      { field: 'Path', label: t('activity.columnPath'), sortable: true, minWidth: 150 },
      { field: 'ErrorMessage', label: t('activity.columnErrorMessage'), sortable: true, minWidth: 200 },
    ],
    [t, allCompanies]
  );
  const open = useUIStore((state) => state.activityDialogOpen);
  const setOpen = useUIStore((state) => state.setActivityDialogOpen);

  // State for status data
  const [statusItems, setStatusItems] = useState<StatusLogItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Filtering state (matching Vue app functionality)
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [userFilterMode, setUserFilterMode] = useState<'current' | 'all' | 'specific'>('all');
  const [userFilterInput, setUserFilterInput] = useState<string>('');
  const [appFilter, setAppFilter] = useState<string>('');
  const [appFilterInput, setAppFilterInput] = useState<string>('');

  // Sort state
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Status dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<number | undefined>();

  // Copy feedback
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  // Track if this is initial fetch vs auto-refresh
  const isInitialFetch = useRef(true);

  // Auto-refresh interval ref (not state, no re-render needed)
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { addSnackbar } = useSnackbarStore();

  // Initialize dates on mount (matching Vue app behavior)
  useEffect(() => {
    const today = new Date();

    setStartDate(today.toISOString().slice(0, 10));
    setEndDate(today.toISOString().slice(0, 10));
  }, []);

  // Build sort expression for API
  // Note: backend always sorts descending (hardcoded in SQL ROW_NUMBER),
  // so we only send the field name. Direction toggle is kept for future support.
  const getSortExpression = useCallback((): string => {
    if (!sortField) return '';
    return sortField;
  }, [sortField]);

  // Fetch status data
  const fetchStatus = useCallback(async () => {
    if (!activeCompany?.Id || !startDate || !endDate) return;

    setIsLoading(true);
    try {
      const startIndex = page * pageSize;
      const endIndex = startIndex + pageSize;

      const result = await statusService.getStatus({
        companyId: activeCompany.Id,
        startDate: statusService.formatDateForApi(new Date(startDate)),
        endDate: statusService.getDayEnd(endDate),
        currentUserOnly: userFilterMode === 'current',
        appFilter: appFilter,
        startIndex,
        endIndex,
        userFilter: userFilterMode === 'specific' ? userFilterInput : '',
        sort: getSortExpression(),
        allCompanies,
      });

      setStatusItems(result.Results || []);
      setTotalItems(result.Total || 0);
      isInitialFetch.current = false;
    } catch (error) {
      setStatusItems([]);
      setTotalItems(0);
      // Only show error notification for initial fetch, not auto-refresh
      if (isInitialFetch.current) {
        addSnackbar(
          t('activity.fetchFailed'),
          'error',
          error instanceof Error ? error.message : String(error),
          true
        );
        isInitialFetch.current = false;
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeCompany?.Id, startDate, endDate, userFilterMode, userFilterInput, appFilter, page, pageSize, addSnackbar, getSortExpression, t, allCompanies]);

  // Fetch on filter/page changes
  useEffect(() => {
    if (open) {
      fetchStatus();
    }
  }, [open, fetchStatus]);

  // Setup auto-refresh when dialog is open
  useEffect(() => {
    // Clear any existing interval
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }

    // Start auto-refresh only on first page when dialog is open
    if (open && page === 0) {
      autoRefreshIntervalRef.current = setInterval(fetchStatus, 60000);
    }

    // Cleanup on unmount or when deps change
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [open, page, fetchStatus]);

  const getStatusIcon = (status?: string) => {
    if (!status) return null;

    const category = getStatusCategory(status);
    switch (category) {
      case 'processing':
        return <HourglassEmpty sx={{ color: 'info.main' }} />;
      case 'pending':
        return <Schedule sx={{ color: 'grey.600' }} />;
      case 'processed':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'low':
        return <CheckCircleOutline sx={{ color: 'success.main' }} />;
      case 'high':
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'medium':
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    if (!status) return 'default';

    const category = getStatusCategory(status);
    switch (category) {
      case 'processing':
        return 'info';
      case 'pending':
        return 'default';
      case 'processed':
      case 'low':
        return 'success';
      case 'high':
      case 'failed':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'default';
    }
  };

  const isFailedStatus = (status?: string) => {
    return status?.startsWith('Failed') || false;
  };

  const handleStatusDetailsClick = (logId: number) => {
    setSelectedLogId(logId);
    setStatusDialogOpen(true);
  };

  const handleAppFilterKeypress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setAppFilter(appFilterInput);
      setPage(0);
    }
  };

  const handleUserFilterKeypress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setPage(0);
    }
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortClick = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const formatDateTime = (value?: string): string => {
    if (!value) return '';
    // Handle .NET JSON date format: /Date(1234567890000)/
    const dotNetMatch = value.match(/\/Date\((\d+)\)\//);
    if (dotNetMatch) {
      const date = new Date(parseInt(dotNetMatch[1], 10));
      return date.toLocaleString(i18n.language);
    }
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString(i18n.language);
    }
    return value;
  };

  const handleCopyRow = (item: StatusLogItem) => {
    const lines = [
      t('activity.copyLineId', { value: item.Id }),
      t('activity.copyLineStatus', { value: item.Status || '' }),
      t('activity.copyLineSourceAppId', { value: item.SourceAppId || '' }),
      t('activity.copyLineSourceSubId', { value: item.SourceSubId || '' }),
      t('activity.copyLineType', { value: item.Type || '' }),
      t('activity.copyLineSubmissionType', { value: item.SubmissionType || '' }),
      t('activity.copyLineApplicationType', { value: item.ApplicationType || '' }),
      t('activity.copyLineProcessDateTime', { value: item.FormattedProcessedDate || '' }),
      t('activity.copyLineUser', { value: item.UserId || '' }),
      t('activity.copyLinePath', { value: item.Path || '' }),
      t('activity.copyLineErrorMessage', { value: item.ErrorMessage || '' }),
      t('activity.copyLineErrorStack', { value: item.ErrorStack || '' }),
      t('activity.copyLineQueueTime', { value: formatDateTime(item.QueueTime) || '' }),
      t('activity.copyLineStartTime', { value: formatDateTime(item.StartTime) || '' }),
    ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopySnackbarOpen(true);
    }).catch(() => {
      addSnackbar(t('activity.copyFailed'), 'error', '', true);
    });
  };

  const handleExport = () => {
    if (!activeCompany?.Id || !startDate || !endDate) return;
    statusService.exportCsv({
      companyId: activeCompany.Id,
      startDate: statusService.formatDateForApi(new Date(startDate)),
      endDate: statusService.getDayEnd(endDate),
      currentUserOnly: userFilterMode === 'current',
      appFilter: appFilter || '',
      userFilter: userFilterMode === 'specific' ? userFilterInput : '',
      allCompanies,
    }).catch(() => {
      addSnackbar(t('activity.exportFailed'), 'error', '', true);
    });
  };

  const getCellValue = (item: StatusLogItem, field: string): string => {
    switch (field) {
      case 'Status': return item.Status || '';
      case 'CompanyName': return item.CompanyName || '';
      case 'SourceAppId': return item.SourceAppId || '';
      case 'SourceSubId': return item.SourceSubId || '';
      case 'Type': return item.Type || '';
      case 'SubmissionType': return item.SubmissionType || '';
      case 'ApplicationType': return item.ApplicationType || '';
      case 'Processed': return item.FormattedProcessedDate || '';
      case 'UserId': return item.UserId || '';
      case 'Path': return item.Path || '';
      case 'ErrorMessage': return item.ErrorMessage || '';
      default: return '';
    }
  };

  return (
    <Dialog
      data-testid="activity-dialog"
      open={open}
      onClose={() => setOpen(false)}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px', maxWidth: '95vw' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} data-testid="activity-dialog-title">
        <Box component="span">{t('activity.title')}</Box>
        <Stack direction="row" spacing={1}>
          <Button
            data-testid="activity-dialog-export-button"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            {t('activity.export')}
          </Button>
          <Button
            data-testid="activity-dialog-refresh-button"
            size="small"
            startIcon={<Refresh />}
            onClick={() => fetchStatus()}
            disabled={isLoading}
          >
            {t('activity.refresh')}
          </Button>
        </Stack>
      </DialogTitle>

      {/* Filters Section - matching Vue ActivityPage */}
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }} data-testid="activity-dialog-filters">
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18nInstance.language.toLowerCase()}>
            <DatePicker
              label={t('activity.startDate')}
              value={startDate ? dayjs(startDate) : null}
              onChange={(val) => {
                setStartDate(val ? val.format('YYYY-MM-DD') : '');
                setPage(0);
              }}
              slotProps={{ textField: { size: 'small', inputProps: { 'data-testid': 'activity-dialog-start-date' } } as any }}
            />
            <DatePicker
              label={t('activity.endDate')}
              value={endDate ? dayjs(endDate) : null}
              minDate={startDate ? dayjs(startDate) : undefined}
              onChange={(val) => {
                setEndDate(val ? val.format('YYYY-MM-DD') : '');
                setPage(0);
              }}
              slotProps={{ textField: { size: 'small', inputProps: { 'data-testid': 'activity-dialog-end-date' } } as any }}
            />
          </LocalizationProvider>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('activity.userFilter')}</InputLabel>
            <Select
              data-testid="activity-dialog-user-filter"
              value={userFilterMode}
              label={t('activity.userFilter')}
              onChange={(e) => {
                setUserFilterMode(e.target.value as 'current' | 'all' | 'specific');
                setPage(0);
              }}
            >
              <MenuItem value="current" data-testid="activity-dialog-user-filter-current">{t('activity.currentUser')}</MenuItem>
              <MenuItem value="all" data-testid="activity-dialog-user-filter-all">{t('activity.allUsers')}</MenuItem>
              <MenuItem value="specific" data-testid="activity-dialog-user-filter-specific">{t('activity.specificUser')}</MenuItem>
            </Select>
          </FormControl>
          {userFilterMode === 'specific' && (
            <TextField
              data-testid="activity-dialog-username-input"
              label={t('activity.username')}
              size="small"
              value={userFilterInput}
              onChange={(e) => setUserFilterInput(e.target.value)}
              onKeyUp={handleUserFilterKeypress}
              onBlur={() => {
                setPage(0);
              }}
              sx={{ minWidth: 150 }}
            />
          )}
          <TextField
            data-testid="activity-dialog-app-filter"
            label={t('activity.applicationNumber')}
            size="small"
            value={appFilterInput}
            onChange={(e) => setAppFilterInput(e.target.value)}
            onKeyUp={handleAppFilterKeypress}
            onBlur={() => {
              if (appFilterInput !== appFilter) {
                setAppFilter(appFilterInput);
                setPage(0);
              }
            }}
            sx={{ minWidth: 180 }}
          />
          {canSeeAllCompanies && (
            <Tooltip title={t('activity.allCompaniesTooltip')}>
              <FormControlLabel
                data-testid="activity-dialog-all-companies"
                control={
                  <Checkbox
                    checked={allCompanies}
                    onChange={(e) => {
                      setAllCompanies(e.target.checked);
                      setPage(0);
                    }}
                    size="small"
                  />
                }
                label={t('activity.allCompanies')}
              />
            </Tooltip>
          )}
        </Stack>
      </Box>

      <DialogContent data-testid="activity-dialog-content" sx={{ p: 0 }}>
        {isLoading && statusItems.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4} data-testid="activity-dialog-loading">
            <CircularProgress data-testid="activity-dialog-loading-spinner" />
          </Box>
        ) : (
          <>
            {/* Activity Table */}
            {statusItems.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }} data-testid="activity-dialog-empty">
                {t('activity.noRecentActivity')}
              </Typography>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 380px)' }} data-testid="activity-dialog-table">
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ minWidth: 48 }} />
                      {columns.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{ minWidth: col.minWidth, whiteSpace: 'nowrap' }}
                        >
                          {col.sortable ? (
                            <TableSortLabel
                              active={sortField === col.field}
                              direction={sortField === col.field ? sortDirection : 'desc'}
                              onClick={() => handleSortClick(col.field)}
                            >
                              {col.label}
                            </TableSortLabel>
                          ) : (
                            col.label
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ minWidth: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusItems.map((item, index) => (
                      <TableRow
                        key={item.Id}
                        hover
                        data-testid={`activity-dialog-item-${index}`}
                        sx={{
                          '&:hover .copy-icon': { visibility: 'visible' },
                        }}
                      >
                        <TableCell padding="checkbox">
                          {getStatusIcon(item.Status)}
                        </TableCell>
                        {columns.map((col) => (
                          <TableCell key={col.field} sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {col.field === 'Status' ? (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={item.Status || t('activity.unknownStatus')}
                                  color={getStatusColor(item.Status)}
                                  variant="filled"
                                />
                                {isFailedStatus(item.Status) && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleStatusDetailsClick(item.Id)}
                                    title={t('activity.viewErrorDetails')}
                                  >
                                    <ErrorIcon fontSize="small" color="error" />
                                  </IconButton>
                                )}
                              </Stack>
                            ) : (
                              <Tooltip title={getCellValue(item, col.field)} placement="top-start">
                                <span>{getCellValue(item, col.field)}</span>
                              </Tooltip>
                            )}
                          </TableCell>
                        ))}
                        <TableCell padding="checkbox">
                          <IconButton
                            className="copy-icon"
                            size="small"
                            onClick={() => handleCopyRow(item)}
                            title={t('activity.copyRowTooltip')}
                            sx={{ visibility: 'hidden' }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {totalItems > 0 && (
              <TablePagination
                data-testid="activity-dialog-pagination"
                component="div"
                count={totalItems}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage={t('pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                  t('pagination.displayedRows', { from, to, count: count !== -1 ? count : `>${to}` })
                }
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setOpen(false)} data-testid="activity-dialog-close-button">{t('activity.close')}</Button>
      </DialogActions>

      {/* Status Details Dialog */}
      <StatusDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        logId={selectedLogId}
      />

      {/* Copy feedback */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message={t('dialogs.status.copiedToClipboard')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
}
